import { Box } from "@mui/material";
import { BookmarkBorder as SavedIcon, Add as AddIcon } from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "./ui/v2Tokens";
import { TextButton } from "./ui/primitives";
import { MacroLine } from "./ui/MacroChips";
import ConfirmDialog from "./dialogs/ConfirmDialog";
import SavedMealDialog from "./dialogs/SavedMealDialog";
import { deleteSavedMeal, renameSavedMeal } from "../../../services/NutritionV2Service";
import { useSavedMealsV2 } from "./data/useNutritionV2";
import { listTotals, type SavedMealV2 } from "./model/nutritionV2Model";

/** The company's reusable meals — saved from any menu, usable in any menu. */
const SavedMealsLibrary = () => {
  const { data: savedMeals, loading, reload } = useSavedMealsV2();

  /** Only writes when the name actually changed, so tabbing through a row is not a save. */
  const commitRename = (id: string, current: string, next: string) => {
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;
    void renameSavedMeal(id, trimmed).then(reload);
  };
  const [deleting, setDeleting] = useState<SavedMealV2 | null>(null);
  // `null` = closed. A meal = editing it. `"new"` = creating one — distinguishable from "closed"
  // only because it is a third state, which is why this is not just a boolean plus a meal.
  const [editing, setEditing] = useState<SavedMealV2 | "new" | null>(null);

  return (
    <Box
      sx={{
        background: v2.surface,
        border: `1px solid ${v2.line}`,
        borderRadius: v2.rLg,
        boxShadow: v2.shadow,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          // the title, its subtitle and the "ארוחה חדשה" button do not fit one line at 281px —
          // the button wraps under them rather than being squeezed to nothing
          flexWrap: "wrap",
          rowGap: "10px",
          padding: { xs: "12px 14px", md: "14px 18px" },
          borderBottom: `1px solid ${v2.line}`,
          background: v2.canvas,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: v2.surface,
            border: `1px solid ${v2.line}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SavedIcon sx={{ color: v2.ink2, fontSize: 20 }} />
        </Box>
        <Box>
          <Box sx={{ fontSize: 15, fontWeight: 800, color: v2.ink }}>ארוחות שמורות</Box>
          <Box sx={{ fontSize: 11.5, color: v2.ink3, fontWeight: 600 }}>
            {savedMeals.length} ארוחות · זמינות לכל המאמנים בחברה
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        {/* A saved meal used to be creatable only as a by-product of building someone's menu, which
            meant a company could not assemble its own library up front. */}
        <Box
          component="button"
          type="button"
          onClick={() => setEditing("new")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            background: v2.brand,
            color: "#fff",
            height: "36px",
            px: "14px",
            borderRadius: v2.rSm,
            fontSize: "13.5px",
            fontWeight: 500,
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "background .12s",
            "&:hover": { background: v2.brandDark },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          ארוחה חדשה
        </Box>
      </Box>

      {savedMeals.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            gap: 1,
            padding: "40px",
          }}
        >
          <SavedIcon sx={{ fontSize: 46, color: v2.line }} />
          {/* "loading" and "you have none" are different answers — saying the second while
              still fetching sends the trainer off to create something they already have */}
          <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.ink3 }}>
            {loading ? "טוען ארוחות שמורות…" : "אין ארוחות שמורות"}
          </Box>
          <Box sx={{ fontSize: 13, color: v2.ink3, textAlign: "center" }}>
            בתוך תפריט, פתח את תפריט הפעולות של ארוחה ובחר "שמור כארוחה שמורה".
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {savedMeals.map((saved) => {
            const totals = listTotals(saved.items);
            return (
              <Box
                key={saved.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                  padding: "12px 18px",
                  borderBottom: `1px solid ${v2.lineSoft}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", flex: 1 }}>
                  <Box
                    component="input"
                    type="text"
                    aria-label={`שם הארוחה השמורה ${saved.name}`}
                    defaultValue={saved.name}
                    /*
                     * Committed on blur / Enter, not on every keystroke.
                     *
                     * Against localStorage a per-character write was free; against the API it is one
                     * request per letter, and worse — the list re-renders from the server's answer,
                     * so a response landing mid-word would yank the caret back and overwrite what
                     * had been typed since. `defaultValue` leaves the field uncontrolled so the
                     * trainer's typing is never contested.
                     */
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => commitRename(saved.id, saved.name, e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    sx={{
                      minWidth: "150px",
                      flex: { xs: "1 1 100%", md: "0 1 auto" },
                      border: "none",
                      background: "transparent",
                      // 16px on a phone, or iOS Safari zooms in on focus and never zooms back
                      fontSize: { xs: "16px", md: "13.5px" },
                      fontWeight: 700,
                      fontFamily: "inherit",
                      color: v2.ink,
                      outline: "none",
                      /* The only thing saying this is editable is a border that appears on focus —
                         which on a touch device means it says nothing at all until after you have
                         guessed. On a phone the underline is always there. */
                      borderBottom: { xs: `1.5px solid ${v2.line}`, md: "1.5px solid transparent" },
                      "&:focus": { borderBottomColor: v2.brand },
                    }}
                  />
                  <Box sx={{ fontSize: "11.5px", color: v2.ink3 }}>{saved.items.length} פריטים</Box>
                  <MacroLine totals={totals} size={11} />
                </Box>
                <Box sx={{ display: "flex", gap: "4px" }}>
                  <TextButton onClick={() => setEditing(saved)} sx={{ padding: "3px 10px" }}>
                    ערוך
                  </TextButton>
                  <TextButton tone="danger" onClick={() => setDeleting(saved)} sx={{ padding: "3px 10px" }}>
                    מחק
                  </TextButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <SavedMealDialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        meal={editing === "new" ? null : editing}
        onSaved={reload}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="למחוק את הארוחה השמורה?"
        message={`"${deleting?.name}" תוסר מהארוחות השמורות של החברה ולא תהיה זמינה לאף מאמן. תפריטים שכבר השתמשו בה לא ישתנו.`}
        confirmLabel="מחק ארוחה"
        onConfirm={() => {
          if (deleting) void deleteSavedMeal(deleting.id).then(reload);
          setDeleting(null);
        }}
      />
    </Box>
  );
};

export default SavedMealsLibrary;
